import { Fragment, useState } from "react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { cn } from "@/lib/utils";
import { ClassNameValue } from "tailwind-merge";

// Base field type
export type BaseFieldType<T> = {
  name: Path<T>;
  placeholder?: string;
  label?: string;
  labelSrOnly?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  required?: boolean;
  disabled?: boolean;
};

// Text field type
export type TextFieldType<T> = BaseFieldType<T> & {
  type: "text" | "email" | "password";
};

// Number field type
export type NumberFieldType<T> = BaseFieldType<T> & {
  type: "number";
  min?: number;
  max?: number;
  step?: number;
  allowDecimals?: boolean;
};

// Currency field type
export type CurrencyFieldType<T> = BaseFieldType<T> & {
  type: "currency";
  currencyCode?: string; // Default: "USD"
};

// Phone field type
export type PhoneFieldType<T> = BaseFieldType<T> & {
  type: "phone";
  phoneTypes?: string[]; // Default: ["mobile", "home", "work"]
  format?: "US" | "international"; // Default: "US"
};

// DateTime field type
export type DateTimeFieldType<T> = BaseFieldType<T> & {
  type: "datetime-local";
};

// Union type for all field types
export type FieldType<T> =
  | TextFieldType<T>
  | NumberFieldType<T>
  | CurrencyFieldType<T>
  | PhoneFieldType<T>
  | DateTimeFieldType<T>;

// Utility functions for formatting

const formatCurrencyCalculator = (cents: number): string => {
  const dollars = cents / 100;
  // Ensure two decimal places, then convert to string and return with commas ever 3
  return dollars.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPhoneNumber = (
  value: string,
  format: "US" | "international" = "US"
): string => {
  if (format !== "US") return value;

  const digits = value.replace(/\D/g, "");
  if (digits.length === 0) return "";

  if (digits.length <= 3) {
    return `(${digits}`;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

const parsePhoneNumber = (value: string): string => {
  return value.replace(/\D/g, "");
};

type InputFieldsProps<T extends FieldValues> = {
  fields: FieldType<T>[];
  form: UseFormReturn<T>;
  className?: ClassNameValue;
};
export const InputFields = <T extends FieldValues>({
  fields,
  form,
  className,
}: InputFieldsProps<T>) => {
  const renderFieldContent = (fieldType: FieldType<T>, field: any) => {
    switch (fieldType.type) {
      case "number":
        const { min, max, step, allowDecimals } =
          fieldType as NumberFieldType<T>;
        return (
          <Input
            className={"text-primary"}
            type="number"
            min={min}
            max={max}
            step={allowDecimals === false ? 1 : step}
            placeholder={fieldType.placeholder}
            autoComplete={fieldType.autoComplete}
            autoFocus={fieldType.autoFocus}
            {...field}
            onChange={(e) => {
              const value =
                e.target.value === "" ? "" : parseFloat(e.target.value);
              field.onChange(value);
            }}
          />
        );

      case "currency":
        return (
          <Input
            icon={"$"}
            className={"text-right text-primary"}
            type="text"
            placeholder={fieldType.placeholder || "0.00"}
            autoComplete={fieldType.autoComplete}
            autoFocus={fieldType.autoFocus}
            {...field}
            value={
              field.value !== undefined && field.value !== null
                ? formatCurrencyCalculator(field.value)
                : "0.00"
            }
            onKeyDown={(e) => {
              const currentValue = field.value || 0;

              // Handle backspace
              if (e.key === "Backspace") {
                e.preventDefault();
                const newAmount = Math.floor(currentValue / 10);
                field.onChange(newAmount);
                return;
              }

              // Only allow digits
              if (!/^[0-9]$/.test(e.key)) {
                e.preventDefault();
                return;
              }

              // Handle digit input
              e.preventDefault();
              const digit = parseInt(e.key);
              const newAmount = currentValue * 10 + digit;

              // Limit to reasonable amount (999,999.99 = 99999999 cents)
              if (newAmount <= 99999999) {
                field.onChange(newAmount);
              }
            }}
          />
        );

      case "phone":
        const { phoneTypes = ["mobile", "home", "work"], format = "US" } =
          fieldType as PhoneFieldType<T>;
        const [phoneType, setPhoneType] = useState("mobile");

        return (
          <div className="space-y-2">
            <div className="flex overflow-hidden rounded-md border">
              <Select value={phoneType} onValueChange={setPhoneType}>
                <SelectTrigger className="w-20 rounded-none border-0 border-r text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {phoneTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className={"flex-1 border-0 text-primary focus:ring-0"}
                type="tel"
                placeholder={fieldType.placeholder || "(555) 123-4567"}
                autoComplete={fieldType.autoComplete}
                autoFocus={fieldType.autoFocus}
                {...field}
                value={
                  field.value ? formatPhoneNumber(field.value, format) : ""
                }
                onChange={(e) => {
                  const formattedValue = formatPhoneNumber(
                    e.target.value,
                    format
                  );
                  const rawValue = parsePhoneNumber(e.target.value);
                  e.target.value = formattedValue;
                  field.onChange(rawValue);
                }}
              />
            </div>
            <input
              type="hidden"
              name={`${field.name}_type`}
              value={phoneType}
            />
          </div>
        );

      case "datetime-local":
        return (
          <Input
            className={
              "text-primary [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:transition-opacity hover:[&::-webkit-calendar-picker-indicator]:opacity-100"
            }
            type="datetime-local"
            placeholder={fieldType.placeholder}
            autoComplete={fieldType.autoComplete}
            autoFocus={fieldType.autoFocus}
            disabled={fieldType.disabled}
            {...field}
            value={
              field.value
                ? new Date(
                    new Date(field.value).getTime() -
                      new Date(field.value).getTimezoneOffset() * 60000
                  )
                    .toISOString()
                    .slice(0, 16)
                : ""
            }
            onChange={(e) => {
              const newDate = e.target.value ? new Date(e.target.value) : null;
              field.onChange(newDate);
            }}
          />
        );

      default:
        // Handle text, email, password types
        return (
          <Input
            className={"text-primary"}
            type={fieldType.type}
            placeholder={fieldType.placeholder}
            autoComplete={fieldType.autoComplete}
            autoFocus={fieldType.autoFocus}
            {...field}
          />
        );
    }
  };

  return (
    <Fragment>
      {fields.map((fieldType) => (
        <FormField
          key={fieldType.name}
          render={({ field }) => (
            <FormItem className={className as string | undefined}>
              <FormLabel className={cn(fieldType.labelSrOnly && "sr-only")}>
                {fieldType.label}
              </FormLabel>
              <FormControl>{renderFieldContent(fieldType, field)}</FormControl>
              <FormMessage />
            </FormItem>
          )}
          name={fieldType.name}
          control={form.control}
        />
      ))}
    </Fragment>
  );
};
